import { FC, useEffect, useState, ChangeEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { EmotionJSX } from '@emotion/react/types/jsx-namespace';

import { validateEmail } from 'src/utils/validators/validate-email';
import { Currency } from 'src/shared/enum/Currency';
import { routes } from 'src/constants/routes';
import { Building } from 'src/shared/types/building';
import { handlePayment } from 'src/utils/handle-payment';
import { EmailValidationState } from 'src/pages/agreements/enum/EmailValidationState';
import { AgreementsView } from 'src/pages/agreements/agreements.view';
import { AgreementType } from 'src/pages/agreements/types';
import { Agreement } from 'src/pages/agreements/agreement';
import { AgreementContent } from 'src/pages/agreements/agreements-content';
import { BookingTermsContent } from 'src/pages/agreements/booking-terms-content';
import { styles } from 'src/pages/agreements/styles';
import { checkIsEngBuilding } from 'src/utils/is-english-building';

import { AmendBookingData } from '../../../api/amendService';
import { AuthContext } from '../../../providers/auth-context';
import { usePostMicroPaymentProcess } from '../../../lib/hooks/use-post-micro-payment-process';
import { usePostAmendNoAdminFee } from '../../../lib/hooks/use-post-amend-no-admin-fee';
import { ProcessMicroPaymentDto } from '../../../api/paymentService/dto/ProcessMicroPayment.dto';
import { ProcessAmendDto } from '../../../api/amendService/dto/ProcessAmend.dto';
import { RoommateData } from '../../../features/multi-occupancy/types';
import { setAmendSelfServeStorage } from '../../../utils/local-storage-versioning';

interface AmendAgreementsContainerProps {
    building: Building;
    amendData: AmendBookingData;
    vitaRef: string;
    processError: (error: AxiosError) => EmotionJSX.Element;
    handleGoBack: () => void;
    roommates: RoommateData[];
}

export const AmendAgreementsContainer: FC<AmendAgreementsContainerProps> = ({
    building,
    amendData,
    vitaRef,
    processError,
    handleGoBack,
    roommates,
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [termsCheckboxVal, setTermsCheckboxVal] = useState(false);
    const [licenceCheckboxVal, setLicenceCheckboxVal] = useState(false);
    const [termsEmailAddress, setTermsEmailAddress] = useState('');
    const [licenceEmailAddress, setLicenceEmailAddress] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [currentAgreement, setCurrentAgreement] = useState<AgreementType>(AgreementType.Terms);
    const [termsEmailValidationState, setTermsEmailValidationState] = useState(
        EmailValidationState.VALID
    );
    const [licenceEmailValidationState, setLicenceEmailValidationState] = useState(
        EmailValidationState.VALID
    );
    const hasDiscount = !!amendData.payment_plan.promotion;
    const isEnglishBuilding = checkIsEngBuilding(building);
    const userState = useContext(AuthContext);

    const selfServe = searchParams.get('selfServe') === 'true';

    const paymentRequired = Boolean(amendData.admin_fee_required);

    const currency = amendData.room.currency as Currency;

    const {
        mutate: postProcessMicroPayment,
        isPending: isProcessingMicroPayment,
        data: microPaymentData,
        error: errorProcessingMicroPayment,
    } = usePostMicroPaymentProcess();

    const {
        mutate: postAmendNoAdminFee,
        isPending: isPostingAmend,
        isSuccess: successfulAmend,
        error: postAmendError,
    } = usePostAmendNoAdminFee();

    useEffect(() => {
        window.dataLayer.push({
            event: 'go_to_agreements',
        });

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        setAmendSelfServeStorage(selfServe);
    }, [selfServe]);

    const handleEmail = (
        value: string,
        userEmail: string | undefined,
        setValue: (v: string) => void,
        setState: (s: EmailValidationState) => void
    ) => {
        const email = value.trim().toLowerCase();
        setValue(email);

        if (!email.length) {
            setState(EmailValidationState.VALID);
            return;
        }

        if (!validateEmail(email)) {
            setState(EmailValidationState.INVALID_EMAIL);
            return;
        }

        if (!userEmail || email !== userEmail) {
            setState(EmailValidationState.MISMATCHED_EMAIL);
            return;
        }

        setState(EmailValidationState.VALID);
    };

    const processAmendNoAdminFee = async () => {
        const payload = new ProcessAmendDto(vitaRef, null, roommates, selfServe);
        postAmendNoAdminFee(payload);
    };

    const processAdminFee = async () => {
        const payload = new ProcessMicroPaymentDto(
            userState.session?.user.user_metadata.firstName,
            userState.session?.user.user_metadata.lastName,
            userState.session?.user.user_metadata.email,
            amendData.room.room_code,
            amendData.room.academic_year,
            vitaRef
        );

        postProcessMicroPayment(payload);
    };

    const handleSubmit = () => {
        if (amendData.admin_fee_required) {
            processAdminFee();
        } else {
            processAmendNoAdminFee();
        }
    };

    if (microPaymentData) {
        handlePayment(microPaymentData);
    }

    if (successfulAmend) {
        navigate(routes.bookingUpdated.index);
        return null;
    }

    if (postAmendError) {
        return processError(postAmendError);
    }

    const isEmailValidTerms = termsEmailValidationState === EmailValidationState.VALID;
    const isEmailValidLicence = licenceEmailValidationState === EmailValidationState.VALID;

    const isBookingTermsSigned = termsCheckboxVal && !!termsEmailAddress && isEmailValidTerms;
    const isLicenceAgreementSigned =
        licenceCheckboxVal && !!licenceEmailAddress && isEmailValidLicence;

    const isButtonDisabled = !isBookingTermsSigned || !isLicenceAgreementSigned;

    const openDrawer = (type: AgreementType) => {
        setCurrentAgreement(type);
        setDrawerOpen(true);
    };

    const closeDrawer = () => setDrawerOpen(false);

    const goToNextDrawer = () => {
        setCurrentAgreement(AgreementType.Licence);
    };

    const skipDrawer = () => setDrawerOpen(false);

    const emailHandler =
        (setValue: (v: string) => void, setState: (s: EmailValidationState) => void) =>
        (e: ChangeEvent<HTMLInputElement>) =>
            handleEmail(e.target.value, userState.session?.user.email, setValue, setState);

    const handleTermsEmail = emailHandler(setTermsEmailAddress, setTermsEmailValidationState);
    const handleLicenceEmail = emailHandler(setLicenceEmailAddress, setLicenceEmailValidationState);

    const bookingTermsAndConditions = (
        <Agreement
            checkboxVal={termsCheckboxVal}
            emailAddress={termsEmailAddress}
            handleEmailChange={handleTermsEmail}
            handleCheckboxChange={(e: ChangeEvent<HTMLInputElement>) =>
                setTermsCheckboxVal(e.target.checked)
            }
            emailValidationState={termsEmailValidationState}
            title="agreements_booking_terms_and_conditions_title"
            id="terms-email-input"
            subtitle="agreements_please_read_t&c"
            content={<BookingTermsContent building={building} />}
            checkboxContent="agreements_booking_terms_and_conditions_checkbox_content"
            emailContent="agreements_booking_terms_and_conditions_email_content"
            submitButton={
                <Button
                    variant="contained"
                    onClick={goToNextDrawer}
                    css={styles.cta}
                    disabled={!isBookingTermsSigned}
                >
                    {t('agreement_next_agreement')}
                </Button>
            }
            name="terms"
        />
    );

    const licenceAgreement = (
        <Agreement
            checkboxVal={licenceCheckboxVal}
            emailAddress={licenceEmailAddress}
            handleEmailChange={handleLicenceEmail}
            handleCheckboxChange={(e: ChangeEvent<HTMLInputElement>) =>
                setLicenceCheckboxVal(e.target.checked)
            }
            emailValidationState={licenceEmailValidationState}
            title={
                isEnglishBuilding
                    ? 'agreements_common_law_tenancy_title'
                    : 'agreements_licence_agreement_title'
            }
            id="licence-email-input"
            subtitle={
                isEnglishBuilding
                    ? 'agreements_please_read_common_law_tenancy'
                    : 'agreements_please_read_licence_agreement'
            }
            content={<AgreementContent building={building} />}
            checkboxContent={
                isEnglishBuilding
                    ? 'agreements_common_law_tenancy_checkbox_content'
                    : 'agreements_licence_agreement_checkbox_content'
            }
            emailContent={
                isEnglishBuilding
                    ? 'agreements_common_law_tenancy_email_content'
                    : 'agreements_licence_agreement_email_content'
            }
            submitButton={
                <Button
                    variant="contained"
                    onClick={skipDrawer}
                    css={styles.cta}
                    disabled={!isLicenceAgreementSigned}
                >
                    {t('agreements_submit')}
                </Button>
            }
            name="licence-agreement"
        />
    );

    if (!amendData.room) {
        return <Navigate to={routes.failure.index} />;
    }

    const loading = isProcessingMicroPayment || isPostingAmend;
    const error = postAmendError || errorProcessingMicroPayment;

    return (
        <AgreementsView
            onGoBack={handleGoBack}
            hideBackButton={!amendData.dual_occupant_amend}
            hasRoommates={false}
            roomData={amendData.room}
            paymentPlan={amendData.payment_plan}
            termDuration={amendData.term_length}
            termStartDate={amendData.start_date}
            termEndDate={amendData.end_date}
            numberOfStudents={amendData.number_of_students}
            currency={currency}
            hasDiscount={hasDiscount}
            paymentRequired={paymentRequired}
            loading={loading}
            error={error}
            isButtonDisabled={isButtonDisabled}
            onSubmit={handleSubmit}
            terms={{
                checkboxVal: termsCheckboxVal,
                email: termsEmailAddress,
                validation: termsEmailValidationState,
                onCheckbox: setTermsCheckboxVal,
                onEmail: handleTermsEmail,
                isValid: isEmailValidTerms,
            }}
            licence={{
                checkboxVal: licenceCheckboxVal,
                email: licenceEmailAddress,
                validation: licenceEmailValidationState,
                onCheckbox: setLicenceCheckboxVal,
                onEmail: handleLicenceEmail,
                isValid: isEmailValidLicence,
            }}
            drawer={{
                open: drawerOpen,
                onClose: closeDrawer,
                current: currentAgreement,
                agreement: currentAgreement,
                total: 2,
            }}
            agreements={{
                terms: bookingTermsAndConditions,
                licence: licenceAgreement,
            }}
            showTermsDrawer={() => openDrawer(AgreementType.Terms)}
            showLicenceDrawer={() => openDrawer(AgreementType.Licence)}
            building={building}
        />
    );
};
